import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { documentId } from "firebase/firestore";
import { filterVisibleProfiles } from "../utils/profileVisibility";

const PAGE_SIZE = 12;

export async function safeCount(q) {
  try {
    const snap = await getCountFromServer(q);
    return snap.data().count || 0;
  } catch {
    return 0;
  }
}

export async function fetchUserDoc(userId) {
  if (!userId) return { blocked: [], shortlisted: [] };
  const snap = await getDoc(doc(db, "users", userId));
  return snap.exists() ? snap.data() : { blocked: [], shortlisted: [] };
}

export async function fetchProfileById(profileId) {
  if (!profileId) return null;
  const snap = await getDoc(doc(db, "profiles", profileId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function fetchProfilesByIds(ids = []) {
  if (!ids?.length) return {};
  const chunks = [];
  for (let i = 0; i < ids.length; i += 10) {
    chunks.push(ids.slice(i, i + 10));
  }

  const snapshots = await Promise.all(
    chunks.map((chunk) => getDocs(query(collection(db, "profiles"), where(documentId(), "in", chunk))))
  );

  return snapshots.flatMap((snap) => snap.docs).reduce((acc, docItem) => {
    acc[docItem.id] = { id: docItem.id, ...docItem.data() };
    return acc;
  }, {});
}

export async function fetchInterestCounts(userId) {
  if (!userId) return { sent: 0, received: 0 };
  const sentQuery = query(collection(db, "interests"), where("fromUserId", "==", userId));
  const receivedQuery = query(collection(db, "interests"), where("toUserId", "==", userId));
  const [sentSnap, receivedSnap] = await Promise.all([
    getCountFromServer(sentQuery).catch(() => getDocs(sentQuery)),
    getCountFromServer(receivedQuery).catch(() => getDocs(receivedQuery)),
  ]);
  return {
    sent: sentSnap.data ? sentSnap.data().count || 0 : sentSnap.size || 0,
    received: receivedSnap.data ? receivedSnap.data().count || 0 : receivedSnap.size || 0,
  };
}

export function buildInterestsQuery(tab, userId, lastVisibleDoc = null) {
  const constraints = [
    where(tab === "received" ? "toUserId" : "fromUserId", "==", userId),
    orderBy("createdAt", "desc"),
  ];
  if (lastVisibleDoc) constraints.push(startAfter(lastVisibleDoc));
  constraints.push(limit(PAGE_SIZE));
  return query(collection(db, "interests"), ...constraints);
}

export async function fetchInterestPage({ queryKey, pageParam = null }) {
  const [, tab, userId] = queryKey;
  const snapshot = await getDocs(buildInterestsQuery(tab, userId, pageParam));
  const docs = snapshot.docs;
  return {
    interests: docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })),
    nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null,
  };
}

export function buildMatchesQuery(targetGender, lastVisibleDoc = null) {
  const constraints = [
    where("gender", "==", targetGender),
    where("profileStatus", "==", "Verified"),
    orderBy("createdAt", "desc"),
  ];

  if (lastVisibleDoc) {
    constraints.push(startAfter(lastVisibleDoc));
  }

  constraints.push(limit(PAGE_SIZE));
  return query(collection(db, "profiles"), ...constraints);
}

export async function fetchMatchesPage({ queryKey, pageParam = null }) {
  const [, targetGender, blockedIds, userId, viewerIsPremium = false] = queryKey;
  const snapshot = await getDocs(buildMatchesQuery(targetGender, pageParam));
  const docs = snapshot.docs;
  const profiles = docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));

  const visibleProfiles = filterVisibleProfiles(
    profiles.filter((profile) => profile.id !== userId),
    blockedIds || [],
    viewerIsPremium
  );

  return {
    profiles: visibleProfiles,
    nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null,
  };
}

export function getServerTimestamp() {
  return serverTimestamp();
}
